import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isEmptyHtml } from '@aero-judge/shared';
import { displayCountryCode } from '@aero-judge/utils';
import { Layout } from '../components/Layout';
import { Hero } from '../components/Hero';
import { OrganizingTeamSection } from '../components/OrganizingTeam';
import { EventFeedPreview } from '../components/EventFeed';
import { PartnersSection } from '../components/PartnersSection';
import { useCompetition, useEventFeed, useResults } from '../hooks/useCompetition';
import { competitionPath, fetchEventInfo, fetchOfficials, fetchSponsors } from '../lib/api';
import { sanitizePublicHtml } from '../lib/rich-html';
import { pilotFullName, formatScore } from '../lib/utils';

export function HomePage() {
  const { competitionId } = useParams<{ competitionId: string }>();
  const { data: competition, isLoading, error } = useCompetition();
  const { data: results } = useResults('OVERALL');
  const { data: feed = [] } = useEventFeed();
  const {
    data: officials = [],
    isLoading: officialsLoading,
    error: officialsError,
  } = useQuery({
    queryKey: ['public-officials', competitionId],
    queryFn: () => fetchOfficials(competitionId!),
    enabled: Boolean(competitionId),
  });
  const {
    data: sponsors = [],
    isLoading: sponsorsLoading,
    error: sponsorsError,
  } = useQuery({
    queryKey: ['public-sponsors', competitionId],
    queryFn: () => fetchSponsors(competitionId!),
    enabled: Boolean(competitionId),
  });
  const { data: eventInfo } = useQuery({
    queryKey: ['public-event-info', competitionId],
    queryFn: () => fetchEventInfo(competitionId!),
    enabled: Boolean(competitionId) && Boolean(competition?.hasInfo),
  });

  if (!competitionId) {
    return (
      <Layout>
        <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
          <h1 className="font-display text-4xl text-foreground">Missing competition</h1>
          <Link to="/" className="mt-4 text-sky-700 dark:text-sky-400 hover:underline">
            Browse competitions
          </Link>
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-sky-400 border-t-transparent" />
        </div>
      </Layout>
    );
  }

  if (error || !competition) {
    return (
      <Layout>
        <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
          <h1 className="font-display text-4xl text-foreground">Competition Not Found</h1>
          <p className="mt-4 text-muted-foreground">This competition may not be published or does not exist.</p>
          <Link to="/" className="mt-6 text-sky-700 dark:text-sky-400 hover:underline">
            Browse competitions
          </Link>
        </div>
      </Layout>
    );
  }

  const topPilots =
    results?.rankings
      .filter((r) => r.pilot)
      .slice(0, 3)
      .map((r) => ({
        rank: r.rank,
        name: pilotFullName(r.pilot!.firstName, r.pilot!.lastName),
        score: formatScore(r.totalScoreCm),
        country: displayCountryCode(r.pilot!.country, r.pilot!.nationality) || '—',
      })) ?? [];

  const aboutHtml =
    eventInfo && !isEmptyHtml(eventInfo.aboutHtml)
      ? sanitizePublicHtml(eventInfo.aboutHtml)
      : '';

  return (
    <Layout>
      <Hero competition={competition} competitionId={competitionId} topPilots={topPilots} />
      <EventFeedPreview competitionId={competitionId} items={feed} />
      {aboutHtml ? (
        <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="font-display text-xl font-semibold text-foreground sm:text-2xl">About</h2>
            <Link
              to={`${competitionPath(competitionId, 'event')}#info`}
              className="text-sm font-medium text-sky-700 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300"
            >
              Full event info →
            </Link>
          </div>
          <div
            className="rich-html mt-5 max-w-3xl text-[15px] leading-relaxed text-muted-foreground line-clamp-[12] [&_a]:text-sky-700 dark:[&_a]:text-sky-700 dark:text-sky-300 [&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-foreground [&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:text-lg [&_h3]:font-semibold [&_li]:my-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: aboutHtml }}
          />
        </section>
      ) : null}
      <OrganizingTeamSection
        competitionId={competitionId}
        officials={officials}
        organiser={competition.organiser}
        limit={4}
        isLoading={officialsLoading}
        error={officialsError instanceof Error ? officialsError : null}
      />
      <PartnersSection
        partners={sponsors}
        label={competition.settings?.partnersLabel?.trim() || 'Sponsors'}
        tiersEnabled={competition.settings?.partnerTiersEnabled ?? true}
        isLoading={sponsorsLoading}
        error={sponsorsError instanceof Error ? sponsorsError : null}
      />
    </Layout>
  );
}
